import { useParams } from 'react-router'
import { Button, Card, Pill, Stack, Text } from '@/design-system'
import { BentoGrid, BentoSpan } from '@/components/Bento'
import { StatTile } from '@/components/StatTile'
import { PageHeader } from '@/app/layouts/PageHeader'
import { NotFoundScreen } from '@/features/system/NotFoundScreen'
import { HOLDINGS } from './crypto-data'
import checkout from '@/app/layouts/CheckoutLayout.module.css'
import { BRAND } from '@/brand/brand'

const HISTORY: Record<string, readonly (readonly [string, string])[]> = {
  USDC: [
    ['Bought', '+1,000.00 USDC'],
    ['Received · Base', '+2,120.00 USDC'],
    ['Converted to USD', '−400.00 USDC'],
  ],
  BTC: [
    ['Bought', '+0.0250 BTC'],
    ['Bought', '+0.0162 BTC'],
  ],
  ETH: [
    ['Bought', '+0.6000 ETH'],
    ['Received · Base', '+0.3120 ETH'],
  ],
}

export function CryptoAssetScreen() {
  const { symbol = '' } = useParams()
  const asset = HOLDINGS.find((item) => item.symbol === symbol.toUpperCase())
  if (!asset) return <NotFoundScreen />

  const history = HISTORY[asset.symbol] ?? []
  const up = asset.change >= 0

  return (
    <>
      <PageHeader
        title={asset.name}
        subtitle={`${asset.amount} ${asset.symbol} · held on ${asset.network}`}
        actions={
          <>
            <Button variant="ghost" size="md" iconStart="south_west" to="/crypto/receive">
              Receive
            </Button>
            <Button variant="primary" size="md" iconStart="add" to={`/crypto/buy?asset=${asset.symbol}`}>
              Buy
            </Button>
          </>
        }
      />

      <BentoGrid>
        {[
          {
            label: 'Your balance',
            value: `$${asset.valueUsd.toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
            note: `${asset.amount} ${asset.symbol}`,
          },
          {
            label: 'Price',
            value: `$${asset.priceUsd.toLocaleString('en-US')}`,
            note: `${up ? '+' : '−'}${Math.abs(asset.change * 100).toFixed(2)}% in 24h`,
          },
          { label: 'Network', value: asset.network, note: 'Gas paid at cost' },
          { label: 'Convert fee', value: '0.10%', note: `Into any ${BRAND.name} balance` },
        ].map((stat) => (
          <StatTile key={stat.label} stat={stat} />
        ))}

        <BentoSpan span={2}>
          <Card tone="light">
            <Card.Header>
              <Text variant="caption" weight="semibold">
                Your activity
              </Text>
              <Pill tone="neutral">{history.length}</Pill>
            </Card.Header>
            <Stack gap={0} className="pb-mt-3">
              {history.map(([label, value]) => (
                <div key={label + value} className={checkout.line}>
                  <Stack grow gap={0}>
                    <Text variant="caption" weight="semibold">
                      {label}
                    </Text>
                    <Text variant="eyebrow" tone="secondary">
                      Settled
                    </Text>
                  </Stack>
                  <Text variant="caption" weight="semibold" numeric>
                    {value}
                  </Text>
                </div>
              ))}
            </Stack>
          </Card>
        </BentoSpan>

        <BentoSpan span={2}>
          <Card tone="dark">
            <Text variant="caption" weight="semibold" tone="onDark">
              What you should know
            </Text>
            <Text variant="caption" tone="onDarkMuted" className="pb-mt-2">
              {asset.symbol === 'USDC'
                ? 'USDC is backed one to one by dollars and short-term treasuries. It is still not a bank deposit and it is not insured.'
                : `${asset.name} is volatile. Its value can fall sharply, including to zero, and nothing here is insured or guaranteed.`}
            </Text>
            <Card.Footer>
              <Button size="md" block iconEnd="arrow_forward" to="/crypto/convert">
                Convert to spendable dollars
              </Button>
            </Card.Footer>
          </Card>
        </BentoSpan>
      </BentoGrid>
    </>
  )
}
